import React, {useCallback, useContext, useEffect, useRef, useState} from 'react';
import {useMatch, useNavigate} from 'react-router-dom';
import {fetchItemById} from '../../http/itemsAPI';
import {addItemToCart, deleteItemFromCart} from '../../http/cartAPI';
import SpinnerDark from '../UI/SpinnerDark';
import {Context} from '../../index';
import {observer} from 'mobx-react-lite';
import {itemImageSrc} from '../../util/imageUrl';
import {addToGuestCart, readGuestCart} from '../../util/guestCart';
import {catalogItemId} from '../../util/catalogItemId';
import {effectiveStockCap} from '../../util/cartLineAvailable';
import {CATALOG_ROUTE, ITEM_ROUTE} from '../../util/Constants';

const PLACEHOLDER = 'https://placehold.co/800x800/f7f4ed/284a38?text=Lutica';

function notifyCartPages() {
    window.dispatchEvent(new CustomEvent('lutica-cart-changed'));
}

const Item = observer(() => {
    /** Рендер вне `<Route path="/items/:id">` — `useParams` не даёт `id`; берём из текущего URL. */
    const itemPathMatch = useMatch({path: `${ITEM_ROUTE}/:id`, end: true});
    const id = itemPathMatch?.params?.id;
    const navigate = useNavigate();
    const {user, cart} = useContext(Context);
    const [item, setItem] = useState(null);
    const [isLoading, setLoading] = useState(true);
    const [slide, setSlide] = useState(0);
    const [zoomOpen, setZoomOpen] = useState(false);
    const [cartHint, setCartHint] = useState('');
    const [plusWarn, setPlusWarn] = useState(false);
    const plusWarnTimerRef = useRef(null);

    const close = useCallback(() => {
        if (window.history.length > 1) {
            navigate(-1);
        } else {
            navigate(CATALOG_ROUTE);
        }
    }, [navigate]);

    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, []);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') {
                close();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [close]);

    useEffect(() => {
        const invalid =
            id == null ||
            id === 'undefined' ||
            String(id).trim() === '' ||
            !Number.isFinite(Number(id));
        if (invalid) {
            setItem(null);
            setLoading(false);
            return undefined;
        }
        let cancelled = false;
        setLoading(true);
        fetchItemById(id)
            .then((data) => {
                if (!cancelled) setItem(data);
            })
            .catch(() => {
                if (!cancelled) setItem(null);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [id]);

    useEffect(() => {
        setSlide(0);
        setZoomOpen(false);
    }, [item]);

    const pid = item ? catalogItemId(item) : null;
    const pidNum = pid != null ? Number(pid) : NaN;
    const inCart = pid != null ? cart.qtyForItem(pid) : 0;
    const cartLine =
        item && !Number.isNaN(pidNum)
            ? cart.lines.find((l) => Number(l.itemId ?? l.id) === pidNum)
            : null;
    const maxStock = item ? effectiveStockCap(item.count, cartLine) : 0;

    const flashPlusLimit = () => {
        setPlusWarn(true);
        if (plusWarnTimerRef.current) {
            window.clearTimeout(plusWarnTimerRef.current);
        }
        plusWarnTimerRef.current = window.setTimeout(() => setPlusWarn(false), 700);
    };

    const addOne = () => {
        if (!item || maxStock <= 0) return;
        if (inCart >= maxStock) {
            flashPlusLimit();
            return;
        }
        setCartHint('');
        if (!user.isAuth) {
            addToGuestCart(item, 1);
            cart.setTotalFromLines(readGuestCart());
            notifyCartPages();
            return;
        }
        addItemToCart(pid)
            .then(() => cart.refreshTotals(true))
            .then(() => notifyCartPages())
            .catch(() => {
                flashPlusLimit();
            });
    };

    const removeOne = () => {
        if (!item || inCart <= 0) return;
        setCartHint('');
        if (!user.isAuth) {
            addToGuestCart(item, -1);
            cart.setTotalFromLines(readGuestCart());
            notifyCartPages();
            return;
        }
        deleteItemFromCart(pid)
            .then(() => cart.refreshTotals(true))
            .then(() => notifyCartPages())
            .catch(() => setCartHint('Не удалось изменить корзину.'));
    };

    if (isLoading) {
        return (
            <div
                className="fixed inset-0 z-[60] flex items-center justify-center bg-bark-900/40 backdrop-blur-sm"
                role="dialog"
                aria-modal="true"
                aria-busy="true"
                aria-label="Loading product"
            >
                <SpinnerDark/>
            </div>
        );
    }

    if (!item) {
        return (
            <div
                className="fixed inset-0 z-[60] flex items-center justify-center bg-bark-900/40 p-4 backdrop-blur-sm"
                role="dialog"
                aria-modal="true"
                aria-labelledby="item-missing-title"
            >
                <div className="relative w-full max-w-md rounded-3xl bg-cream-50 p-8 text-center shadow-2xl">
                    <h2 id="item-missing-title" className="text-lg font-semibold text-bark-900">
                        Товар не найден
                    </h2>
                    <button type="button" className="lut-btn-pine mt-6" onClick={close}>
                        Закрыть
                    </button>
                </div>
            </div>
        );
    }

    const slides = [];
    const n = Math.max(1, (item.imageUrls?.length || item.images?.length || 0));
    for (let i = 0; i < n; i++) {
        const s = itemImageSrc(item, i);
        if (s) slides.push(s);
    }
    if (slides.length === 0) slides.push(PLACEHOLDER);

    const currentSrc = slides[Math.min(slide, slides.length - 1)] || PLACEHOLDER;
    const canSlide = slides.length > 1;
    const showPrev = () => {
        setSlide((s) => (s <= 0 ? slides.length - 1 : s - 1));
    };
    const showNext = () => {
        setSlide((s) => (s + 1) % slides.length);
    };

    return (
        <div
            className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center sm:p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="item-modal-title"
        >
            <button
                type="button"
                className="absolute inset-0 bg-bark-900/50 backdrop-blur-[2px]"
                aria-label="Close"
                onClick={close}
            />
            <div
                className="relative z-10 flex max-h-[min(100dvh,920px)] w-full max-w-5xl flex-col overflow-hidden rounded-t-[2rem] border border-stone-200/80 bg-cream-50 shadow-2xl sm:max-h-[90vh] sm:rounded-[2rem]"
            >
                <div className="flex items-center justify-end border-b border-stone-200/70 px-4 py-3 sm:px-6">
                    <button
                        type="button"
                        onClick={close}
                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-2xl leading-none text-bark-500 hover:bg-cream-200 touch-manipulation"
                        aria-label="Close"
                    >
                        ×
                    </button>
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-[max(1rem,env(safe-area-inset-bottom))]">
                    <div className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-4 py-6 sm:px-8 lg:flex-row lg:items-start lg:gap-12">
                        <div className="mx-auto w-full max-w-sm shrink-0 sm:max-w-md lg:mx-0 lg:max-w-md">
                            <div className="relative aspect-square w-full overflow-hidden rounded-[1.75rem] border border-stone-200/60 bg-cream-100 shadow-inner">
                                {canSlide && (
                                    <button
                                        type="button"
                                        onClick={showPrev}
                                        className="absolute left-2 top-1/2 z-10 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full bg-white/65 p-0 text-3xl leading-none text-bark-700 shadow hover:bg-white/80"
                                        aria-label="Previous photo"
                                    >
                                        <span className="relative -top-px">‹</span>
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={() => setZoomOpen(true)}
                                    className="block h-full w-full"
                                    aria-label="Open larger image"
                                >
                                    <img
                                        key={currentSrc}
                                        src={currentSrc}
                                        alt={item.title}
                                        className="h-full w-full object-contain object-center"
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = PLACEHOLDER;
                                        }}
                                    />
                                </button>
                                {canSlide && (
                                    <button
                                        type="button"
                                        onClick={showNext}
                                        className="absolute right-2 top-1/2 z-10 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full bg-white/65 p-0 text-3xl leading-none text-bark-700 shadow hover:bg-white/80"
                                        aria-label="Next photo"
                                    >
                                        <span className="relative -top-px">›</span>
                                    </button>
                                )}
                            </div>
                            {slides.length > 1 && (
                                <div className="mt-4 flex justify-center gap-2">
                                    {slides.map((src, i) => (
                                        <button
                                            key={src + i}
                                            type="button"
                                            aria-label={`Photo ${i + 1}`}
                                            onClick={() => setSlide(i)}
                                            className={`h-2 rounded-full transition-all ${
                                                i === slide ? 'w-8 bg-pine-700' : 'w-2 bg-stone-300 hover:bg-stone-400'
                                            }`}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="flex flex-1 flex-col lg:pt-2">
                            <h2
                                id="item-modal-title"
                                className="text-xl font-semibold leading-snug text-bark-900 sm:text-2xl"
                            >
                                {item.title}
                            </h2>
                            <p className="mt-4 text-3xl font-medium text-pine-800">
                                {item.price}
                                <span className="ml-1 text-lg font-normal text-bark-500">₽</span>
                            </p>

                            <div className="mt-6 max-w-xs">
                                {maxStock > 0 ? (
                                    inCart > 0 ? (
                                        <div className="flex h-11 w-full items-center justify-center gap-2">
                                            <button
                                                type="button"
                                                onClick={removeOne}
                                                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-stone-200/90 text-xl font-semibold leading-none text-bark-700 hover:bg-cream-100 touch-manipulation"
                                                aria-label="Remove one"
                                            >
                                                −
                                            </button>
                                            <span className="min-w-[2.5rem] text-center text-base font-semibold tabular-nums text-bark-900">
                                                {inCart}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={addOne}
                                                className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-xl font-semibold leading-none transition-colors touch-manipulation ${
                                                    plusWarn
                                                        ? 'border-0 bg-red-600 text-white ring-2 ring-red-400 animate-pulse'
                                                        : inCart >= maxStock
                                                          ? 'border border-stone-200 bg-stone-200 text-bark-300'
                                                          : 'border-0 bg-pine-700 text-white hover:bg-pine-800'
                                                }`}
                                                aria-label="Add one more"
                                            >
                                                +
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={addOne}
                                            className="lut-btn-pine flex h-11 w-full items-center justify-center text-base font-semibold shadow-md shadow-pine-900/15 touch-manipulation"
                                        >
                                            В корзину
                                        </button>
                                    )
                                ) : (
                                    <p className="flex min-h-11 items-center rounded-2xl border border-stone-200 bg-stone-50 px-4 text-sm text-bark-500">
                                        Нет в наличии
                                    </p>
                                )}
                            </div>
                            {cartHint ? <p className="mt-2 text-sm text-red-600">{cartHint}</p> : null}

                            <div className="mt-10 border-t border-stone-200/80 pt-8">
                                <p className="max-w-prose leading-relaxed text-bark-700">{item.description}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {zoomOpen && (
                <div
                    className="absolute inset-0 z-30 flex items-center justify-center bg-black/80 p-4"
                    onClick={() => setZoomOpen(false)}
                >
                    <div className="relative inline-block max-h-full max-w-full" onClick={(e) => e.stopPropagation()}>
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                setZoomOpen(false);
                            }}
                            className="absolute right-3 top-3 z-20 grid h-10 w-10 place-items-center rounded-full bg-white/70 p-0 text-3xl leading-none text-bark-700 hover:bg-white/85"
                            aria-label="Close enlarged image"
                        >
                            <span className="relative -top-px">×</span>
                        </button>
                        {canSlide && (
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    showPrev();
                                }}
                                className="absolute left-3 top-1/2 z-20 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full bg-white/70 p-0 text-4xl leading-none text-bark-700 hover:bg-white/85"
                                aria-label="Previous photo"
                            >
                                <span className="relative -top-px">‹</span>
                            </button>
                        )}
                        <img
                            src={currentSrc}
                            alt={`${item.title} (увеличенное)`}
                            className="max-h-[calc(100vh-3rem)] max-w-[calc(100vw-3rem)] object-contain"
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = PLACEHOLDER;
                            }}
                        />
                        {canSlide && (
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    showNext();
                                }}
                                className="absolute right-3 top-1/2 z-20 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full bg-white/70 p-0 text-4xl leading-none text-bark-700 hover:bg-white/85"
                                aria-label="Next photo"
                            >
                                <span className="relative -top-px">›</span>
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
});

export default Item;
